import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';

import { Badge, PageHeader, PreviewBanner } from '@/components/ui/page';
import { SortTh } from '@/components/ui/sort-th';
import { table } from '@/components/ui/styles';
import { LINK_METHOD_LABELS, MOCK_CALLS, NOTE_TAKER_LABELS, type MockCall } from '@/mocks/calls';
import { leadSearchLink } from '@/mocks/shared';
import { formatDateTime } from '@/utils/format';
import { dateValue, useSort, type SortColumns } from '@/utils/use-sort';

import { NOTE_TONES, leadName, staffName } from './call-helpers';

const COLUMNS: SortColumns<MockCall> = {
  call: { value: (c) => dateValue(c.startedAt), desc: true },
  lead: { value: (c) => leadName(c.leadEmail) },
  duration: { value: (c) => c.durationMin, desc: true },
};

/**
 * Calls (CALL-1…4, planned for V2): calls with clients, each linked to its lead
 * automatically, even when the client joined with another email (CALL-2), with the
 * note taker's status (CALL-4; the tool is still open, Q14). Preview: every call is made up.
 * A phone shows the calls as a list of cards instead of the table (UI-1).
 */
const CallsPage = () => {
  const navigate = useNavigate();
  const open = (id: string) => navigate(`/calls/${id}`);
  const { rows, sort, toggle } = useSort(MOCK_CALLS, COLUMNS);

  return (
    <div>
      <PageHeader title="Calls" description="Calls with clients, linked to the lead automatically, with the note taker’s notes." />
      <PreviewBanner requirements={['CALL-1…4', 'Q14']}>
        Planned for V2. Not connected to data yet: every call here is made up, and the note-taker tool is still to be chosen.
      </PreviewBanner>

      <div className={table.wrap}>
        <ul className="md:hidden">
          {rows.map((c) => (
            <li key={c.id} className="border-b border-line last:border-0">
              <Link to={`/calls/${c.id}`} className="block px-4 py-3 transition-colors hover:bg-surface-hover">
                <div className="flex items-start justify-between gap-3">
                  <p className="min-w-0 text-sm text-fg-strong">{c.title}</p>
                  <Badge tone={NOTE_TONES[c.noteTaker]}>{NOTE_TAKER_LABELS[c.noteTaker]}</Badge>
                </div>
                <p className="mt-0.5 text-xs text-fg-muted">
                  {formatDateTime(c.startedAt)} · {c.durationMin} min
                </p>
                <p className="mt-1 text-sm text-fg">
                  {leadName(c.leadEmail)} <span className="text-fg-muted">with {staffName(c.staffId)}</span>
                </p>
                <p className="mt-0.5 text-xs text-fg-muted">Linked: {LINK_METHOD_LABELS[c.link.method]}</p>
              </Link>
            </li>
          ))}
        </ul>
        <div className="hidden overflow-x-auto md:block">
          <table className={classNames(table.table, 'min-w-[900px]')}>
            <thead className={table.head}>
              <tr>
                <SortTh label="Call" column="call" sort={sort} onSort={toggle} />
                <SortTh label="Lead" column="lead" sort={sort} onSort={toggle} />
                <th scope="col" className={table.th}>Team</th>
                <SortTh label="Duration" column="duration" sort={sort} onSort={toggle} right />
                <th scope="col" className={table.th}>Linked by</th>
                <th scope="col" className={table.th}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr
                  key={c.id}
                  tabIndex={0}
                  onClick={() => open(c.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      open(c.id);
                    }
                  }}
                  className={classNames(table.rowLink, 'align-top focus:outline-none focus-visible:bg-surface-hover')}
                >
                  <td className={table.td}>
                    <p className="text-fg-strong">{c.title}</p>
                    <p className="text-xs text-fg-muted">{formatDateTime(c.startedAt)}</p>
                  </td>
                  <td className={table.td}>
                    <p className="text-fg">{leadName(c.leadEmail)}</p>
                    <Link
                      to={leadSearchLink(c.leadEmail)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      className="text-xs text-fg-muted hover:text-brand"
                    >
                      {c.leadEmail}
                    </Link>
                  </td>
                  <td className={classNames(table.td, 'whitespace-nowrap text-fg-muted')}>{staffName(c.staffId)}</td>
                  <td className={classNames(table.td, 'whitespace-nowrap text-right tabular-nums text-fg')}>{c.durationMin} min</td>
                  <td className={classNames(table.td, 'max-w-xs')}>
                    <p className="text-fg">{LINK_METHOD_LABELS[c.link.method]}</p>
                    <p className="line-clamp-2 text-xs text-fg-muted" title={c.link.detail}>
                      {c.link.detail}
                    </p>
                  </td>
                  <td className={table.td}>
                    <Badge tone={NOTE_TONES[c.noteTaker]}>{NOTE_TAKER_LABELS[c.noteTaker]}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="border-t border-line px-4 py-3 text-sm text-fg-muted">{MOCK_CALLS.length} calls</p>
      </div>
    </div>
  );
};

export default CallsPage;
